import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { range } from 'lit/directives/range.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  type Lobby,
  type LobbyCategory,
  type LobbyCategoryDisplay,
  type LobbyGame,
  type Studio,
  TileType,
} from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { FeatureFlag } from '../../_ui-core_/base/package/services/package/features/feature-service';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import sortProviderByGamesNumber from '../../_ui-core_/base/package/util/providers-game-number-sort';
import { debounce } from '../../_ui-core_/base/package/util/radash-helper';
import type { FilterItem, FilterSelectDetail } from '../../_ui-core_/components/filter/ui-filter';
import { UISearchBarState } from '../../_ui-core_/components/ui-search-bar/ui-search-bar';
import App from '../../app';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import { OFTEN_SEARCHED_CATEGORY_SLUG } from '../../constants';
import placeholderStyles from '../../styles/placeholder.css?inline';
import { CategoryPageId } from '../category/category-page';
import style from './search-page.css?inline';
import '../../_ui-core_/components/carousels/ui-studios-carousel';
import '../../_ui-core_/components/icons/ui-no-search-result-icon';
import '../../_ui-core_/components/ui-placeholder/ui-placeholder';
import '../../_ui-core_/components/ui-screen-reader-only/ui-screen-reader-only';
import '../../_ui-core_/components/ui-section-header/ui-section-header';
import { isDesktopWidth } from '../../_ui-core_/base/package/util/device';

const CName = 'search-page';
const TILE_TYPE = TileType.P1;

@customElement(CName)
export class SearchPage extends LitElement {
  static readonly styles = [unsafeCSS(placeholderStyles), unsafeCSS(style)];

  @state() private _emptySearchResults = 0;
  @state() private _filterOptions?: FilterItem[] = [];
  @state() private _isSearchRequested = false;
  @state() private _lobby: Lobby;
  @state() private _searchBarState: UISearchBarState = UISearchBarState.INACTIVE;
  @state() private _searchBarValue = '';
  @state() private _searchResultGames: LobbyGame[] | undefined = [];
  @state() private _sortedStudios: Studio[] = [];
  @state() private _oftenSearchedGames?: LobbyGame[];
  @state() private _allowBestMatch = false;
  @state() private _isBestMatchComponentLoaded = false;

  private _searchCategories: LobbyCategory[];
  private _searchAbortController: AbortController | null = null;
  private _renderTaskId: number | null = null;

  private readonly _debouncedSearch = debounce({ delay: 500 }, this._doSearch.bind(this));
  private readonly _pagePaddingInline = 'var(--page-padding-inline)';
  private readonly _subHelper = new SubHelper();

  connectedCallback() {
    super.connectedCallback();
    // Skip in the desktop slide-in: the render task would hide the background
    // page (main-slot + footer), while the sidebar itself is not gated by it.
    if (!window.$app.router.getIsSidebarOpen()) {
      this._renderTaskId = window.$app.renderService.addTask('search-page');
    }
    this._subHelper.addSub(App.instance.content.getLobby(), (_) => this._updateLobby(_), true);
    window.$app.features.getFeature(FeatureFlag.BestMatchEnabled).then((isBestMatchEnabled) => {
      if (!this.isConnected) return;
      this._allowBestMatch = !!isBestMatchEnabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
    this._removeRenderTask();
    this._debouncedSearch.cancel();
    this._searchAbortController?.abort();
    this._searchAbortController = null;
  }

  protected firstUpdated() {
    this._updateHeader();
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('_allowBestMatch')) {
      if (this._allowBestMatch && window.$app.casinoConfig.bestMatch && !this._isBestMatchComponentLoaded) {
        import('../../_ui-core_/components/ui-best-match-banner/ui-best-match-banner').then(() => {
          this._isBestMatchComponentLoaded = true;
        });
      }
    }
  }

  render() {
    return html`
      <main>
        <ui-a11y-title>${window.$app.$t.get(I18n.a11y.heading.search)}</ui-a11y-title>
        <ui-screen-reader-only role="note">${window.$app.$t.get(I18n.a11y.note.search)}</ui-screen-reader-only>
        <ui-search-bar
          class="search-bar"
          state=${this._searchBarState}
          @ui-search-bar-input=${this._handleInput}
          @focus=${() => {
            window.$app.track.searchAttempt(TrackingEventSource.SEARCH_PAGE);
            window.$app.vaix.trackSearchBarClickVaix('search');
          }}
          placeholder=${window.$app.$t.get(I18n.base.searchFor)}
        ></ui-search-bar>
        ${this._shouldShowResults() ? this._renderSearchResultsSection() : nothing}
        ${
          this._searchCategories
            ? repeat(
                this._searchCategories,
                (c) => c.filterId,
                (c) => this._renderCategory(c),
              )
            : nothing
        }
        ${this._renderSectionTitle(window.$app.$t.get(I18n.base.studios), CategoryPageId.STUDIOS)}
        ${this._renderStudioUI()} ${this._renderFilter()}
      </main>
    `;
  }

  private _renderSearchResultsSection() {
    if (this._searchResultGames === undefined) {
      return html`
        ${this._renderSearchAriaLive()}
        <ui-section-header title=${window.$app.$t.get(I18n.base.searchResults)} class="inline"></ui-section-header>
        ${this._renderPlaceholder()}
      `;
    }

    if (this._searchResultGames.length) {
      return html`
        ${this._renderSearchAriaLive()}
        <ui-section-header title=${window.$app.$t.get(I18n.base.searchResults)} class="inline"></ui-section-header>
        ${this._renderGames(this._searchResultGames)}
      `;
    }

    if (this._isSearchRequested) {
      return this._renderNoResultsFound();
    }

    return nothing;
  }

  private _renderSearchAriaLive() {
    return html`
      <div aria-live="polite" class="a11y-search-feedback" role="status">
        ${window.$app.$t.get(I18n.a11y.status.searchResults, {
          count: String(this._searchResultGames?.length),
          searchTerm: this._searchBarValue,
        })}
      </div>
    `;
  }

  private _renderPlaceholder() {
    return html`
      <div class="placeholder ph-tiles">
        ${map(range(4), () => html`<ui-placeholder .type=${TileType.P1}></ui-placeholder>`)}
      </div>
    `;
  }

  private _renderFilter() {
    if (this._filterOptions === undefined || this._filterOptions.length === 0) {
      return nothing;
    }
    return html`
      <ui-section-header class="nested" title=${window.$app.$t.get(I18n.base.categories)}></ui-section-header>
      <ui-filter class="search" @select=${this._handleFilterSelection} .items=${this._filterOptions} href></ui-filter>
    `;
  }

  private _renderCategory(category: LobbyCategory) {
    const isOftenSearched = category.slug === OFTEN_SEARCHED_CATEGORY_SLUG;
    const games = this._getCategoryGames(category);
    if (games === undefined) {
      return isOftenSearched
        ? html` ${this._renderSectionTitle(category.title, category.slug)} ${this._renderPlaceholder()} `
        : nothing;
    }
    if (games.length === 0) {
      return nothing;
    }
    return html`
      ${this._renderSectionTitle(category.title, category.slug)} ${this._renderGamesUi(category, games)}
      ${isOftenSearched ? this._renderBestMatchCTA() : nothing}
    `;
  }

  private _renderBestMatchCTA() {
    if (!this._allowBestMatch || !window.$app.casinoConfig.bestMatch || !this._isBestMatchComponentLoaded) {
      return nothing;
    }
    return html`<ui-best-match-banner></ui-best-match-banner>`;
  }

  private _renderGamesUi(category: LobbyCategory, games: LobbyGame[]) {
    return html`
      <ui-games-carousel
        class="carousel"
        padding=${this._pagePaddingInline}
        .display=${category.display}
        .games=${games}
        .trackingSource=${TrackingEventSource.SEARCH_PAGE}
        .categorySlug=${category.slug}
        @ui-games-carousel-click=${() =>
          window.$app.track.searchFollowUp({ eventAction: 'followUpFrequentlySearched' })}
      ></ui-games-carousel>
    `;
  }

  private _renderStudioUI() {
    if (this._sortedStudios?.length) {
      return html`
        <ui-studios-carousel
          class="carousel"
          padding=${this._pagePaddingInline}
          .studios=${this._sortedStudios}
          @ui-provider-tile-click=${(e: CustomEvent) => {
            window.$app.track.searchFollowUp({ eventAction: 'followUpProvider', searchProvider: e.detail.id });
            e.stopImmediatePropagation();
          }}
        ></ui-studios-carousel>
      `;
    }
    return nothing;
  }

  private _renderNoResultsFound() {
    return html`
      <div class="no-result">
        ${this._renderNotFoundIcon()}
        <div>${window.$app.$t.get(I18n.base.noResultsFound)}</div>
      </div>
    `;
  }

  private _renderGames(games: LobbyGame[]) {
    const display: LobbyCategoryDisplay = {
      limit: 20,
      rank: false,
      rows: 1,
      shouldShowLabels: true,
      tile: TILE_TYPE,
    };

    return html`<ui-games-carousel
      class="carousel"
      .games=${games}
      .display=${display}
      .trackingSource=${TrackingEventSource.SEARCH_PAGE}
      .categorySlug=${'base.searchResults'}
      padding=${this._pagePaddingInline}
      @ui-games-carousel-click=${() => window.$app.track.searchFollowUp({ eventAction: 'followUpResult' })}
    ></ui-games-carousel>`;
  }

  private _renderNotFoundIcon() {
    return html`<ui-no-search-result-icon class="no-result-icon"></ui-no-search-result-icon>`;
  }

  private _renderSectionTitle(title: string, slug: string) {
    const navigateTo = (link: string) => {
      if (link === CategoryPageId.STUDIOS) {
        App.instance.router.navigateToProviders();
      } else {
        App.instance.router.navigateToCategory(link);
      }
    };

    const action = {
      text: window.$app.$t.get(I18n.base.viewAll),
      callback: () => navigateTo(slug),
    };

    return html`<ui-section-header class="nested" title=${title} .action=${action}></ui-section-header>`;
  }

  private _handleFilterSelection(ev: CustomEvent<FilterSelectDetail>) {
    const { name: label, slug } = ev.detail;
    window.$app.track.searchFollowUp({ eventAction: 'followUpCategory', searchCategory: label });
    App.instance.router.navigateToTag(slug);
  }

  private _handleInput(event: CustomEvent) {
    const rawInput = (event.target as HTMLInputElement).value;
    const trimmedInput = rawInput.trim().replace(/\s+/g, ' ');
    const tokens = trimmedInput.split(' ').filter((t) => t.length >= App.instance.appSettings.minSearchTermLength);

    this._searchBarValue = rawInput;

    if (tokens.length === 0) {
      this._searchBarState = UISearchBarState.INACTIVE;
      this._isSearchRequested = false;
      this._searchResultGames = undefined;
      return;
    }

    this._searchBarState = UISearchBarState.SEARCHING;
    this._isSearchRequested = true;
    this._debouncedSearch(trimmedInput);
  }

  private async _doSearch(value: string) {
    this._searchAbortController?.abort();
    const controller = new AbortController();
    this._searchAbortController = controller;

    try {
      this._searchBarState = UISearchBarState.SEARCHING;
      this._isSearchRequested = true;
      this._searchResultGames = undefined; // IGM-765 reset the array to ensure embla carousel recognizes the update
      this._searchResultGames = await App.instance.content.searchGames(value, TILE_TYPE);
      if (controller.signal.aborted) return;

      this._searchBarState = UISearchBarState.INACTIVE;

      if (this._searchResultGames.length) {
        window.$app.track.searchResult({
          eventAction: 'searchSuccess',
          searchTerm: value,
          searchSource: TrackingEventSource.SEARCH_PAGE,
        });
      } else {
        this._emptySearchResults++;
        window.$app.track.searchResult({
          eventAction: 'searchFail',
          searchTerm: value,
          searchSource: TrackingEventSource.SEARCH_PAGE,
          errorMessage: window.$app.$t.get(I18n.base.noResultsFound),
          frequency: this._emptySearchResults,
        });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      window.$app.logger.error('Error during search', err);
      this._searchResultGames = undefined;
      this._searchBarState = UISearchBarState.ERRORED;
      window.$app.track.searchResult({
        eventAction: 'searchError',
        searchTerm: value,
        searchSource: TrackingEventSource.SEARCH_PAGE,
        errorMessage: window.$app.$t.get(I18n.base.noResultsFound),
        frequency: this._emptySearchResults,
      });
    }
  }

  private _shouldShowResults(): boolean {
    return this._isSearchRequested;
  }

  private _getCategoryGames(category: LobbyCategory) {
    return category.slug === OFTEN_SEARCHED_CATEGORY_SLUG ? this._oftenSearchedGames : category.games;
  }

  private _updateLobby(lobby?: Lobby) {
    if (!lobby) {
      return;
    }

    this._lobby = { ...lobby };

    // Filter
    this._searchCategories = this._lobby.searchCategories;
    this._filterOptions = this._lobby.searchTags;

    // Studios
    if (lobby.studios?.length) {
      this._sortedStudios = sortProviderByGamesNumber(lobby.studios);
    }

    this._fetchOftenSearchedGames().finally(async () => {
      await this.updateComplete;
      this._removeRenderTask();
    });
    this._trackGamesImpression();
  }

  private _removeRenderTask(): void {
    if (this._renderTaskId !== null) {
      this._renderTaskId = window.$app.renderService.removeTask(this._renderTaskId);
    }
  }

  private _fetchOftenSearchedGames(): Promise<void> {
    if (!this._searchCategories?.some((category) => category.slug === OFTEN_SEARCHED_CATEGORY_SLUG)) {
      return Promise.resolve();
    }
    return App.instance.content
      .getOftenSearchedGames(TILE_TYPE)
      .then((games) => {
        this._oftenSearchedGames = games;
      })
      .catch((err) => {
        window.$app.logger.warn('[often-searched] fetch failed', err);
        this._oftenSearchedGames = [];
      });
  }

  private _trackGamesImpression() {
    const mappedCategories = this._searchCategories.map((category) => ({
      location: category.slug,
      gameSlugs: category.games ? category.games.map((game) => game.slug) : [''],
    }));

    window.$app.vaix.trackGamesImpressionVaix(mappedCategories);
  }

  private _updateHeader() {
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: window.$app.$t.get(I18n.base.search),
      trackingSource: TrackingEventSource.SEARCH_PAGE,
      isGoBackShown: isDesktopWidth(),
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: SearchPage;
  }
}
